import { useCallback, useState } from 'react';
import { useInvalidQuestions } from './useInvalidQuestions';
import { useQuestionnaireNavigation } from '../context/NavigationProvider';

interface UseQuestionnaireValidationOptions {
  onValidationSuccess?: () => void;
  onValidationError?: (invalidQuestions: string[]) => void;
}

interface UseQuestionnaireValidationReturn {
  validateAndProceed: () => void;
  showErrorMessage: boolean;
  setShowErrorMessage: (show: boolean) => void;
  resetErrors: () => void;
  hasInvalidQuestions: boolean;
}

export const useQuestionnaireValidation = (
  options: UseQuestionnaireValidationOptions = {},
): UseQuestionnaireValidationReturn => {
  const { onValidationSuccess, onValidationError } = options;

  const { showErrors, setShowErrors } = useQuestionnaireNavigation();
  const [showErrorMessage, setShowErrorMessage] = useState(false);

  const invalidQuestions = useInvalidQuestions();

  const resetErrors = useCallback(() => {
    setShowErrors(false);
    setShowErrorMessage(false);
  }, [setShowErrors]);

  const validateAndProceed = useCallback(() => {
    if (invalidQuestions.length) {
      if (showErrors === true) {
        setShowErrorMessage(true);
      } else {
        setShowErrors(true);
      }

      const element = document.getElementById(
        'question_' + invalidQuestions[0],
      );
      element?.scrollIntoView({ behavior: 'smooth' });

      onValidationError?.(invalidQuestions);
      return;
    }

    resetErrors();
    onValidationSuccess?.();
  }, [
    invalidQuestions,
    showErrors,
    setShowErrors,
    resetErrors,
    onValidationSuccess,
    onValidationError,
  ]);

  return {
    validateAndProceed,
    showErrorMessage,
    setShowErrorMessage,
    resetErrors,
    hasInvalidQuestions: invalidQuestions.length > 0,
  };
};
